import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Info, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MortgageData } from '@/types/calculator';
import { EducationalDialog } from './EducationalDialog';
import { Alert, AlertDescription } from "@/components/ui/alert";

interface TexasPropertyTaxCardProps {
  mortgageData: MortgageData;
}

export const TexasPropertyTaxCard: React.FC<TexasPropertyTaxCardProps> = ({ mortgageData }) => {
  const [showDialog, setShowDialog] = useState(false);

  const estimatedAnnualTax = mortgageData.propertyTax * 12;
  const monthlyTax = mortgageData.propertyTax.toFixed(0);

  return (
    <>
      <Card className="h-full bg-card border-border">
        <CardHeader className="pb-2 md:pb-3">
          <CardTitle className="text-sm md:text-base flex items-center gap-1.5 md:gap-2">
            <Building2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary flex-shrink-0" />
            <span className="hidden sm:inline">Texas Property Taxes & Assessments</span>
            <span className="sm:hidden">TX Property Tax</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 md:space-y-3">
          <Alert className="bg-primary/5 border-primary/20 py-2 md:py-3">
            <DollarSign className="h-3 w-3 md:h-4 md:w-4 text-primary" />
            <AlertDescription className="text-xs md:text-sm">
              Est. tax: <strong>${monthlyTax}/mo</strong> (${estimatedAnnualTax}/year)
            </AlertDescription>
          </Alert>

          <div className="space-y-2 md:space-y-3">
            <div className="p-2 md:p-3 rounded-lg bg-muted/30">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1 flex items-center gap-2">
                Property Tax Overview
              </h4>
              <p className="text-[10px] md:text-xs text-muted-foreground">
                No state income tax. Avg rate: 2.4%. Funds schools, county services, cities.
              </p>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1 flex items-center gap-1.5 md:gap-2">
                <span className="bg-amber-500 text-white px-1 md:px-1.5 py-0.5 rounded text-[10px] md:text-xs">PID</span>
                <span className="text-xs md:text-sm">Public Improvement District</span>
              </h4>
              <p className="text-[10px] md:text-xs text-muted-foreground mb-1 md:mb-2">
                Separate assessment (not tax-deductible) for amenities. Typical: 0.54% annually.
              </p>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• Landscaping, centers, maintenance</li>
                <li>• Duration: 20-30 years</li>
                <li>• Can't be waived (even VA 100%)</li>
                <li>• Common in master-planned</li>
              </ul>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1 flex items-center gap-1.5 md:gap-2">
                <span className="bg-blue-500 text-white px-1 md:px-1.5 py-0.5 rounded text-[10px] md:text-xs">MUD</span>
                <span className="text-xs md:text-sm">Municipal Utility District</span>
              </h4>
              <p className="text-[10px] md:text-xs text-muted-foreground mb-1 md:mb-2">
                Water, sewer, drainage. Typical: 0.3-1.0%.
              </p>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• Common in Houston/DFW suburbs</li>
                <li>• Absorbed by city (10-20 years)</li>
                <li>• May increase home value</li>
              </ul>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1">Tax Exemptions</h4>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• <strong>Homestead:</strong> $100K+ reduction</li>
                <li>• <strong>Over-65:</strong> Extra + ceiling</li>
                <li>• <strong>VA 100%:</strong> Tax waived (not PID/MUD)</li>
                <li>• <strong>Spouse:</strong> Maintains exemptions</li>
              </ul>
            </div>
          </div>

          <Button 
            variant="outline" 
            size="sm" 
            className="w-full h-8 text-xs md:text-sm"
            onClick={() => setShowDialog(true)}
          >
            <Info className="w-3 h-3 mr-1.5 md:mr-2" />
            <span className="hidden sm:inline">Learn More About Texas Property Taxes</span>
            <span className="sm:hidden">Learn More</span>
          </Button>
        </CardContent>
      </Card>

      <EducationalDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        title="Complete Guide to Texas Property Taxes"
      >
        <div className="space-y-4">
          <section>
            <h4 className="font-semibold mb-2">How Property Taxes Are Calculated</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Property Tax = (Appraised Value - Exemptions) × Tax Rate
            </p>
            <p className="text-sm text-muted-foreground">
              Appraisal districts assess your home's value annually (usually January 1st). You can protest if you disagree.
              Tax rates are set by local entities and vary by county, school district, and city.
            </p>
          </section>

          <section>
            <h4 className="font-semibold mb-2">What Your Taxes Fund</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• <strong>School Districts (50-60%):</strong> Teacher salaries, facilities, programs</li>
              <li>• <strong>County (20-25%):</strong> Roads, law enforcement, courts, hospitals</li>
              <li>• <strong>Cities (15-20%):</strong> Fire, police, parks, utilities</li>
              <li>• <strong>Special Districts (5-10%):</strong> Colleges, hospitals, water/sewer</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">PID vs. MUD: Key Differences</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Feature</th>
                    <th className="text-left py-2">PID</th>
                    <th className="text-left py-2">MUD</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b">
                    <td className="py-2">Purpose</td>
                    <td>Amenities, maintenance</td>
                    <td>Water, sewer, drainage</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Typical Rate</td>
                    <td>0.54% in Texas</td>
                    <td>0.3-1.0%</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Tax Deductible?</td>
                    <td>No</td>
                    <td>Yes</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Duration</td>
                    <td>20-30 years</td>
                    <td>Until city annexation</td>
                  </tr>
                  <tr>
                    <td className="py-2">VA Waiver?</td>
                    <td>No</td>
                    <td>No</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Payment Timeline</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• <strong>January 1:</strong> Appraisal date (property value assessed)</li>
              <li>• <strong>April-May:</strong> Appraisal notices mailed</li>
              <li>• <strong>May 15:</strong> Protest deadline (can extend to May 31 if notice late)</li>
              <li>• <strong>October:</strong> Tax bills mailed</li>
              <li>• <strong>January 31:</strong> Payment deadline (avoid penalties)</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Homestead Exemption Benefits</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Must file by April 30th of the tax year. Benefits include:
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• School district exemption: $100,000 (in 2023, increases annually)</li>
              <li>• Optional city/county exemptions: varies by location</li>
              <li>• 10% cap on assessed value increases per year</li>
              <li>• Protection from forced sale for most debts</li>
            </ul>
          </section>
        </div>
      </EducationalDialog>
    </>
  );
};
